import { useMemo } from 'react';
import { useUserRole } from './useUserRole';

export interface Permissions {
  // Conversas
  conversations: {
    view: boolean;
    viewAll: boolean; // Gestor vê todas, Atendente só suas
    send: boolean;
    assign: boolean; // Apenas Gestor
    transfer: boolean;
    delete: boolean; // Apenas Admin
  };
  
  // CRM/Leads
  crm: {
    view: boolean;
    viewAll: boolean; // Gestor vê todos, Atendente só atribuídos
    create: boolean;
    edit: boolean;
    delete: boolean; // Apenas Gestor/Admin
    export: boolean; // Apenas Gestor/Admin
    viewSensitiveFields: boolean; // Apenas Gestor/Admin
  };
  
  // Dashboard
  dashboard: {
    view: boolean;
    viewMetrics: boolean; // Apenas Gestor/Admin
    viewAnalytics: boolean; // Apenas Gestor/Admin
    viewReports: boolean; // Apenas Gestor/Admin
    viewTeamMetrics: boolean; // Apenas Gestor/Admin
  };
  
  // Kanban
  kanban: {
    view: boolean;
    viewAll: boolean; // Gestor vê todos, Atendente só atribuídos
    move: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean; // Apenas Gestor/Admin
    assign: boolean; // Apenas Gestor/Admin
  };
  
  // Automação
  automation: {
    view: boolean;
    create: boolean; // Apenas Gestor/Admin
    edit: boolean; // Apenas Gestor/Admin
    delete: boolean; // Apenas Admin
    execute: boolean; // Apenas Gestor/Admin
  };
  
  // Equipe
  team: {
    view: boolean; // Apenas Gestor/Admin
    invite: boolean; // Apenas Gestor/Admin
    remove: boolean; // Apenas Admin
    changeRole: boolean; // Apenas Admin
    viewPerformance: boolean; // Apenas Gestor/Admin
  };
  
  // Configurações
  settings: {
    view: boolean;
    edit: boolean; // Apenas Admin
    billing: boolean; // Apenas Admin
    integrations: boolean; // Apenas Admin
    api: boolean; // Apenas Admin
  };
  
  // Relatórios
  reports: {
    view: boolean; // Apenas Gestor/Admin
    create: boolean; // Apenas Gestor/Admin
    export: boolean; // Apenas Gestor/Admin
    schedule: boolean; // Apenas Admin
  };
  
  // AI Training
  aiTraining: {
    view: boolean;
    edit: boolean; // Apenas Gestor/Admin
    delete: boolean; // Apenas Admin
  };
}

export function usePermissions(): Permissions {
  const { role, isManager, isAdmin } = useUserRole();
  
  return useMemo(() => {
    const permissions: Permissions = {
      conversations: {
        view: true,
        viewAll: isManager || isAdmin,
        send: true,
        assign: isManager || isAdmin,
        transfer: isManager || isAdmin,
        delete: isAdmin,
      },
      crm: {
        view: true,
        viewAll: isManager || isAdmin,
        create: true,
        edit: true,
        delete: isManager || isAdmin,
        export: isManager || isAdmin,
        viewSensitiveFields: isManager || isAdmin,
      },
      dashboard: {
        view: true,
        viewMetrics: isManager || isAdmin,
        viewAnalytics: isManager || isAdmin,
        viewReports: isManager || isAdmin,
        viewTeamMetrics: isManager || isAdmin,
      },
      kanban: {
        view: true,
        viewAll: isManager || isAdmin,
        move: true,
        create: true,
        edit: true,
        delete: isManager || isAdmin,
        assign: isManager || isAdmin,
      },
      automation: {
        view: true,
        create: isManager || isAdmin,
        edit: isManager || isAdmin,
        delete: isAdmin,
        execute: isManager || isAdmin,
      },
      team: {
        view: isManager || isAdmin,
        invite: isManager || isAdmin,
        remove: isAdmin,
        changeRole: isAdmin,
        viewPerformance: isManager || isAdmin,
      },
      settings: {
        view: true,
        edit: isAdmin,
        billing: isAdmin,
        integrations: isAdmin,
        api: isAdmin,
      },
      reports: {
        view: isManager || isAdmin,
        create: isManager || isAdmin,
        export: isManager || isAdmin,
        schedule: isAdmin,
      },
      aiTraining: {
        view: true,
        edit: isManager || isAdmin,
        delete: isAdmin,
      },
    };
    
    return permissions;
  }, [role, isManager, isAdmin]);
}

// Helper para verificar permissão específica
export function hasPermission(
  permissions: Permissions,
  category: keyof Permissions,
  action: string
): boolean {
  const categoryPerms = permissions[category] as Record<string, boolean>;
  return categoryPerms[action] ?? false;
}
